import type { MapDocument, MapLocation, MapStand } from "./types";

export type PlacePosition = Pick<MapLocation, "x" | "y">;

function nameForPlace(value: string) {
  const label = value.trim();
  if (!label || label.length > 200)
    throw new Error("Enter a place name of 1–200 characters.");
  return label;
}

function checkedPosition(position: PlacePosition) {
  if (
    !Number.isFinite(position.x) ||
    !Number.isFinite(position.y) ||
    position.x < 0 ||
    position.x > 1 ||
    position.y < 0 ||
    position.y > 1
  )
    throw new Error("Choose a position inside the map.");
  return { x: position.x, y: position.y };
}

/** IDs stay fixed after creation, including when the display name changes. */
export function newPlaceId(document: MapDocument, label: string) {
  const occupied = new Set([
    ...document.locations.map((item) => item.id),
    ...document.geometry.entrances.map((item) => item.id),
    ...document.geometry.stands.map((item) => item.id),
    ...Object.keys(document.labels.entrances),
    ...Object.keys(document.labels.stands),
  ]);
  const name = label
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80)
    .replace(/-+$/g, "");
  const base = name ? `place-${name}` : "place";
  let id = base;
  let suffix = 2;
  while (occupied.has(id)) id = `${base}-${suffix++}`;
  return id;
}

export function defaultPlacePosition(document: MapDocument): PlacePosition {
  const { floor, width, height } = document.geometry;
  return {
    x: (floor.x + floor.width / 2) / width,
    y: (floor.y + floor.height / 2) / height,
  };
}

/** A place has a floor marker and a navigable location. Never mutate the saved input. */
export function addMapPlace(
  document: MapDocument,
  name: string,
  position: PlacePosition,
): { document: MapDocument; location: MapLocation } {
  if (document.geometry.entrances.length >= 100)
    throw new Error(
      "This map already has 100 places. Remove one before adding another.",
    );
  if (document.locations.length >= 3_000)
    throw new Error("This map has reached its location limit.");
  const label = nameForPlace(name);
  const location = {
    id: newPlaceId(document, label),
    label,
    preserveOnRefresh: true,
    ...checkedPosition(position),
  };
  return {
    location,
    document: {
      ...document,
      locations: [...document.locations, location],
      geometry: {
        ...document.geometry,
        entrances: [
          ...document.geometry.entrances,
          {
            id: location.id,
            label: location.label,
            x: location.x * document.geometry.width,
            y: location.y * document.geometry.height,
          },
        ],
      },
      // New places use live text, rather than an obsolete imported lettering path.
      labels: {
        ...document.labels,
        entrances: { ...document.labels.entrances },
      },
    },
  };
}

/** Remove the floor marker, imported lettering and location as one draft edit. */
export function removeMapPlace(document: MapDocument, id: string): MapDocument {
  if (document.geometry.stands.some((item) => item.id === id))
    throw new Error(
      "Booth navigation points must be managed with their booth.",
    );
  if (!document.locations.some((item) => item.id === id)) return document;
  const entrances = { ...document.labels.entrances };
  delete entrances[id];
  return {
    ...document,
    locations: document.locations.filter((item) => item.id !== id),
    geometry: {
      ...document.geometry,
      entrances: document.geometry.entrances.filter((item) => item.id !== id),
    },
    labels: { ...document.labels, entrances },
  };
}

function inside(x: number, y: number, polygon: number[][]) {
  let result = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [ax, ay] = polygon[i];
    const [bx, by] = polygon[j];
    if (ay > y !== by > y && x < ((bx - ax) * (y - ay)) / (by - ay) + ax)
      result = !result;
  }
  return result;
}

/** Prefer the source label's anchor; scan interior strips when it is outside a concave roof. */
export function boothLocationPosition(
  document: MapDocument,
  stand: MapStand,
): PlacePosition {
  if (
    stand.polygons.some((polygon) =>
      inside(stand.label.x, stand.label.y, polygon),
    )
  )
    return {
      x: stand.label.x / document.geometry.width,
      y: stand.label.y / document.geometry.height,
    };
  let best: { x: number; y: number; area: number } | undefined;
  for (const polygon of stand.polygons) {
    const levels = [...new Set(polygon.map((point) => point[1]))].sort(
      (a, b) => a - b,
    );
    for (let level = 1; level < levels.length; level++) {
      const bottom = levels[level];
      const top = levels[level - 1];
      const y = (top + bottom) / 2;
      const intersections: number[] = [];
      for (let index = 0; index < polygon.length; index++) {
        const [ax, ay] = polygon[index];
        const [bx, by] = polygon[(index + 1) % polygon.length];
        if (ay > y !== by > y)
          intersections.push(ax + ((y - ay) * (bx - ax)) / (by - ay));
      }
      intersections.sort((a, b) => a - b);
      for (let index = 0; index + 1 < intersections.length; index += 2) {
        const left = intersections[index];
        const right = intersections[index + 1];
        const area = (right - left) * (bottom - top);
        if (!best || area > best.area)
          best = { x: (left + right) / 2, y, area };
      }
    }
  }
  if (!best)
    throw new Error("This booth has no usable shape for a navigation point.");
  return checkedPosition({
    x: best.x / document.geometry.width,
    y: best.y / document.geometry.height,
  });
}

export function addBoothNavigationLocation(
  document: MapDocument,
  standId: string,
): { document: MapDocument; location: MapLocation } {
  const stand = document.geometry.stands.find((item) => item.id === standId);
  if (!stand) throw new Error("Choose a booth on this map.");
  const existing = document.locations.find((item) => item.id === standId);
  if (existing) return { document, location: existing };
  if (document.locations.length >= 3_000)
    throw new Error("This map has reached its location limit.");
  const location: MapLocation = {
    id: stand.id,
    label:
      (stand.shortName || stand.name || stand.id).trim().slice(0, 200) ||
      stand.id,
    ...boothLocationPosition(document, stand),
  };
  return {
    document: { ...document, locations: [...document.locations, location] },
    location,
  };
}
